import React from 'react';

// Education Badge - Compact and attractive design
export function EducationBadge({ badge, size = 'sm' }) {
  const colorMap = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gold: 'bg-amber-50 text-amber-700 border-amber-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
    amber: 'bg-orange-50 text-orange-700 border-orange-200',
  };

  const sizeMap = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-0.5',
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1.5',
  };

  const iconSizeMap = {
    xs: 'text-[10px]',
    sm: 'text-xs',
    md: 'text-sm',
  };

  const colorClass = colorMap[badge.color] || colorMap.gray;
  const sizeClass = sizeMap[size];
  const iconSize = iconSizeMap[size];

  return (
    <span 
      className={`inline-flex items-center rounded-full border font-medium ${colorClass} ${sizeClass}`}
      title={badge.description}
      data-testid={`badge-${badge.key}`}
    >
      <span className={iconSize}>{badge.icon}</span>
      <span className="truncate max-w-[80px]">{badge.label}</span>
    </span>
  );
}

// Tutor Trust Badges Row - Shows multiple badges compactly
export function TutorTrustBadges({ badges = [], maxDisplay = 3, size = 'sm' }) {
  if (!badges || badges.length === 0) return null;

  const BADGE_DATA = {
    identity_verified: { key: 'identity_verified', label: 'Verified', icon: '✓', color: 'green', description: 'Identity verified' },
    responsive_tutor: { key: 'responsive_tutor', label: 'Responsive', icon: '⚡', color: 'blue', description: 'Quick responses' },
    attendance_reliable: { key: 'attendance_reliable', label: 'Reliable', icon: '★', color: 'gold', description: 'High completion rate' },
    online_teaching_ready: { key: 'online_teaching_ready', label: 'Online', icon: '💻', color: 'purple', description: 'Online setup ready' },
    qualifications_claimed: { key: 'qualifications_claimed', label: 'Qualified', icon: '📜', color: 'gray', description: 'Qualifications claimed' },
    experienced_tutor: { key: 'experienced_tutor', label: 'Experienced', icon: '🎖️', color: 'amber', description: '50+ sessions' },
    social_verified: { key: 'social_verified', label: 'Verified', icon: '✓', color: 'green', description: 'Identity verified' },
    dbs_checked: { key: 'dbs_checked', label: 'DBS', icon: '🛡️', color: 'green', description: 'DBS checked' },
    slts_registered: { key: 'slts_registered', label: 'SLTS', icon: '🇱🇰', color: 'blue', description: 'SLTS registered' },
    qualified_teacher: { key: 'qualified_teacher', label: 'QTS', icon: '🎓', color: 'gold', description: 'Teaching qualification' },
    top_rated: { key: 'top_rated', label: 'Top Rated', icon: '⭐', color: 'gold', description: '4.5+ rating with 10+ reviews' },
  };

  const displayBadges = badges.slice(0, maxDisplay);
  const remaining = badges.length - maxDisplay;

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="tutor-trust-badges">
      {displayBadges.map((badgeKey) => {
        const badge = BADGE_DATA[badgeKey];
        if (!badge) return null;
        return <EducationBadge key={badgeKey} badge={badge} size={size} />;
      })}
      {remaining > 0 && (
        <span className="text-xs text-gray-400 ml-0.5">+{remaining}</span>
      )}
    </div>
  );
}

// Learning Progress Badges
export function LearningProgressBadges({ badges = [], maxDisplay = 4 }) {
  if (!badges || badges.length === 0) return null;

  const BADGE_DATA = {
    trial_completed: { key: 'trial_completed', label: 'Trial Done', icon: '🎯', color: 'green' },
    five_sessions_completed: { key: 'five_sessions_completed', label: '5 Sessions', icon: '⭐', color: 'blue' },
    course_completed: { key: 'course_completed', label: 'Course Done', icon: '🎓', color: 'gold' },
    mock_tests_completed: { key: 'mock_tests_completed', label: 'Practice Pro', icon: '📝', color: 'purple' },
  };

  const displayBadges = badges.slice(0, maxDisplay);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {displayBadges.map((badgeKey) => {
        const badge = BADGE_DATA[badgeKey];
        if (!badge) return null;
        return <EducationBadge key={badgeKey} badge={badge} size="xs" />;
      })}
    </div>
  );
}

export default EducationBadge;
